import React, { useCallback, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  createSizeGuide,
  deleteSizeGuide,
  getAdminSizeGuides,
  updateSizeGuide,
} from '../../services/admin/sizeGuides';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const emptyForm = {
  id: null,
  name: '',
  content: '',
};

const SizeGuidesPage = () => {
  const { toast } = useToast();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchGuides = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getAdminSizeGuides();
      setGuides(data);
    } catch (error) {
      console.error('Error fetching size guides:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Beden rehberleri yüklenirken hata oluştu.',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useAdminAutoRefresh(fetchGuides, { interval: 15000, enabled: !isDialogOpen });

  const handleSave = async () => {
    try {
      if (form.id) {
        await updateSizeGuide(form.id, { name: form.name, content: form.content });
      } else {
        await createSizeGuide({ name: form.name, content: form.content });
      }
      setIsDialogOpen(false);
      setForm(emptyForm);
      fetchGuides();
      toast({
        title: 'Başarılı',
        description: 'Beden rehberi kaydedildi.',
      });
    } catch (error) {
      console.error('Error saving size guide:', error);
      toast({
        title: 'Hata',
        description: 'Beden rehberi kaydedilirken hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSizeGuide(id);
      fetchGuides();
      toast({
        title: 'Başarılı',
        description: 'Beden rehberi silindi.',
      });
    } catch (error) {
      console.error('Error deleting size guide:', error);
      toast({
        title: 'Hata',
        description: 'Beden rehberi silinirken hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Beden Rehberleri</h1>
            <p className="text-gray-600 mt-1">Ürünlerde kullanılacak beden rehberlerini yönetin.</p>
          </div>
          <div className="admin-toolbar">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setForm(emptyForm)}>Yeni Rehber</Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{form.id ? 'Rehberi Düzenle' : 'Yeni Rehber'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Ad</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="content">İçerik (HTML)</Label>
                  <Textarea
                    id="content"
                    rows={10}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="<table>...</table>"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button onClick={handleSave}>Kaydet</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-gray-600">Yükleniyor...</div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">Ad</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {guides.map((guide) => (
                  <tr key={guide.id} className="border-b">
                    <td className="px-4 py-3">{guide.name}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setForm(guide);
                          setIsDialogOpen(true);
                        }}
                      >
                        Düzenle
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(guide.id)}
                      >
                        Sil
                      </Button>
                    </td>
                  </tr>
                ))}
                {guides.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={2}>
                      Henüz rehber yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SizeGuidesPage;
